import { UserInfo } from '@sgwt/react-shared-libs';
import { IItemsFilterProps } from '../../components/shared/filters/ItemsFilter';
import { cleanTribe, isTribeUrlClean, isUrlClean } from '../../helpers';
import { hasValidPeriod } from '../../helpers/record';
import { IAMPermission, RESULT_KEYS, ScopeResultKey } from '../../types';
import { ComplianceQueryParams, FilterQueryParams } from '../../utils/withQueryParams';

const computeUrlParam = (
  param: string,
  fqp: FilterQueryParams,
  filters: IItemsFilterProps[],
  availableDates: string[],
  userPermissions: IAMPermission[],
  userFull: UserInfo<string> | undefined
  // eslint-disable-next-line sonarjs/cognitive-complexity
): string | (string | null)[] | number | null | undefined => {
  const hasManagedByOptions =
    filters.filter((item) => item.queryParamKey === 'managedBy').flatMap((filter) => filter.options).length > 0;

  const hasSgMarketsOptions =
    filters.filter((item) => item.queryParamKey === 'sgMarkets').flatMap((filter) => filter.options).length > 0;

  const hasAmerManagedOptions =
    filters.filter((item) => item.queryParamKey === 'isAmerManaged').flatMap((filter) => filter.options).length > 0;

  const hasFilters = filters.length > 0;

  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (param) {
    case 'sgMarkets':
      return fqp.sgMarkets === undefined || fqp.sgMarkets !== 'good' || !hasSgMarketsOptions
        ? undefined
        : fqp.sgMarkets;

    case 'tribes':
      return typeof fqp.tribes !== 'undefined' && hasFilters && isTribeUrlClean(fqp.tribes, filters)
        ? cleanTribe(fqp.tribes, filters)
        : undefined;

    case 'trigrams':
      return typeof fqp.trigrams !== 'undefined' && hasFilters && !isUrlClean('trigrams', fqp.trigrams, filters)
        ? undefined
        : fqp.trigrams;

    case 'countries':
      return typeof fqp.countries !== 'undefined' && hasFilters && !isUrlClean('countries', fqp.countries, filters)
        ? undefined
        : fqp.countries;

    case 'criticalities':
      return typeof fqp.criticalities !== 'undefined' &&
        hasFilters &&
        !isUrlClean('criticalities', fqp.criticalities, filters)
        ? undefined
        : fqp.criticalities;

    case 'applicationTypes':
      return typeof fqp.applicationTypes !== 'undefined' &&
        hasFilters &&
        !isUrlClean('applicationTypes', fqp.applicationTypes, filters)
        ? undefined
        : fqp.applicationTypes;

    case 'status':
      return typeof fqp.status !== 'undefined' && hasFilters && !isUrlClean('status', fqp.status, filters)
        ? undefined
        : fqp.status;

    case 'tags':
      return typeof fqp.tags !== 'undefined' && hasFilters && !isUrlClean('tags', fqp.tags, filters)
        ? undefined
        : fqp.tags;

    case 'names':
      return typeof fqp.names !== 'undefined' && hasFilters && !isUrlClean('names', fqp.names, filters)
        ? undefined
        : fqp.names;

    case 'architectureTypes':
      return typeof fqp.architectureTypes !== 'undefined' &&
        hasFilters &&
        !isUrlClean('architectureTypes', fqp.architectureTypes, filters)
        ? undefined
        : fqp.architectureTypes;

    case 'functionalArchitecture':
      return fqp.functionalArchitecture;

    case 'isAmerManaged':
      return (fqp.isAmerManaged !== undefined && fqp.isAmerManaged !== '1') || !hasAmerManagedOptions
        ? undefined
        : fqp.isAmerManaged;

    case 'novaCompatibility':
      return typeof fqp.novaCompatibility !== 'undefined' &&
        hasFilters &&
        !isUrlClean('novaCompatibility', fqp.novaCompatibility, filters)
        ? undefined
        : fqp.novaCompatibility;

    case 'isCertified':
      return fqp.isCertified !== undefined && fqp.isCertified !== '1' ? undefined : fqp.isCertified;

    case 'languages':
      return typeof fqp.languages !== 'undefined' && hasFilters && !isUrlClean('languages', fqp.languages, filters)
        ? undefined
        : fqp.languages;

    case 'presenceResults':
      return fqp.presenceResults !== undefined && hasFilters ? fqp.presenceResults : undefined;

    case 'infrastructureComponent':
      return fqp.infrastructureComponent !== undefined && hasFilters ? fqp.infrastructureComponent : undefined;

    case 'enablers':
      return fqp.enablers !== undefined && hasFilters ? fqp.enablers : undefined;

    case 'query':
      return fqp.query ? fqp.query : undefined;

    case 'sort':
      if (location.pathname.includes('reporting')) {
        return undefined;
      }
      return typeof fqp.sort !== 'undefined' && fqp.sort !== null && ['iAppliName', 'createdDate'].includes(fqp.sort)
        ? fqp.sort
        : 'iAppliName';

    case 'order':
      return typeof fqp.order !== 'undefined' && fqp.order !== null && ['ASC', 'DESC'].includes(fqp.order)
        ? fqp.order
        : 'ASC';

    case 'scopeResults':
      return fqp.scopeResults !== undefined && hasFilters ? fqp.scopeResults : undefined;

    case 'resultSort':
      return typeof fqp.resultSort !== 'undefined' &&
        fqp.resultSort !== null &&
        RESULT_KEYS.includes(fqp.resultSort as ScopeResultKey)
        ? fqp.resultSort
        : undefined;

    case 'resultOrder':
      return typeof fqp.resultOrder !== 'undefined' &&
        fqp.resultOrder !== null &&
        ['ASC', 'DESC'].includes(fqp.resultOrder)
        ? fqp.resultOrder
        : undefined;

    case 'resultValue':
      return typeof fqp.resultValue !== 'undefined' &&
        fqp.resultValue !== null &&
        ['good', 'medium', 'bad', 'n/d', 'n/a', 'already-azure', 'in-progress'].includes(fqp.resultValue)
        ? fqp.resultValue
        : undefined;

    case 'managedBy':
      return typeof fqp.managedBy !== 'undefined' && hasFilters && !isUrlClean('managedBy', fqp.managedBy, filters)
        ? undefined
        : fqp.managedBy;

    case 'obsolescenceDate':
      return fqp.obsolescenceDate ? fqp.obsolescenceDate : undefined;

    case 'remediations':
      return location.pathname.includes('explore') ? fqp.remediations : undefined;

    case 'ownershipRc':
      return fqp.ownershipRc;

    case 'managementRc':
      return fqp.managementRc;

    case 'iam':
      return fqp.iam;

    case 'componentStatus':
      return typeof fqp.componentStatus != 'undefined' && isUrlClean('componentStatus', fqp.componentStatus, filters)
        ? fqp.componentStatus
        : undefined;

    case 'periodView':
      return location.pathname.includes('reporting')
        ? undefined
        : hasValidPeriod(fqp.periodView, availableDates)
        ? fqp.periodView
        : hasValidPeriod('LAST_THREE_MONTHS', availableDates)
        ? 'LAST_THREE_MONTHS'
        : 'ALL';

    case 'idType':
      return typeof fqp.idType !== 'undefined' && fqp.idType !== null && ['iAppliID', 'kearID'].includes(fqp.idType)
        ? fqp.idType
        : undefined;

    case 'ownedBy':
      return typeof fqp.ownedBy !== 'undefined' && hasFilters && !isUrlClean('ownedBy', fqp.ownedBy, filters)
        ? undefined
        : fqp.ownedBy;

    case 'remediationTarget':
      return location.pathname.includes('reporting') && fqp.remediationTarget ? fqp.remediationTarget : undefined;

    case 'remediationQuery':
      return location.pathname.includes('reporting') && fqp.remediationQuery ? fqp.remediationQuery : undefined;

    case 'remediationStatus':
      return location.pathname.includes('reporting') && fqp.remediationStatus ? fqp.remediationStatus : undefined;

    case 'remediationComponents':
      return location.pathname.includes('reporting') && fqp.remediationComponents
        ? fqp.remediationComponents
        : undefined;

    case 'assetZones':
      return fqp.assetZones;

    default:
      return undefined;
  }
};

export const computeQueryParameters = (
  fqp: FilterQueryParams & ComplianceQueryParams,
  filters: IItemsFilterProps[],
  availableDates: string[],
  userPermissions: IAMPermission[],
  userFull: UserInfo<string> | undefined
): { computedFilterQueryParams: Record<string, string | (string | null)[] | number | null | undefined> } => {
  const computedFilterQueryParams = Object.keys(fqp).reduce((acc, filter) => {
    const value = computeUrlParam(filter, fqp, filters, availableDates, userPermissions, userFull);
    return { ...acc, [filter]: value };
  }, {});

  return { computedFilterQueryParams };
};
